
import EventPublisher from "../utils/EventPublisher";
import SecureRequestExecuter from "./requestExecuters/SecureRequestExecuter";
import SecureResponseHandler from "./ResponseHandlers/SecureResponseHandler";
import ActionResultEvents from "../constants/events/ActionResultEvents";


class ProfileResponse {

    constructor(
        profileId,
        memberId,
        displayName,
        email,
        phoneNumber,
        about,
        paymentAddressName,
        currentPaymentAddressId,
        currentPaymentAddressGooglePlaceId,
        currentPaymentAddressStreetNumber,
        currentPaymentAddressRoute,
        currentPaymentAddressSecondaryAddress,
        currentPaymentAddressLocality,
        currentPaymentAddressAdministrativeAreaLevel1,
        currentPaymentAddressAdministrativeAreaLevel2,
        currentPaymentAddressCountryLong,
        currentPaymentAddressCountryShort,
        currentPaymentAddressPostalCode,
        currentPaymentAddressFormattedAddress,
        currentPaymentAddressLatitude,
        currentPaymentAddressLongitude,
        created,
        updated,
        deleted
    ) {
        this.profileId = profileId;
        this.memberId = memberId;
        this.displayName = displayName;
        this.email = email;
        this.phoneNumber = phoneNumber;
        this.about = about;
        this.paymentAddressName = paymentAddressName;
        this.currentPaymentAddressId = currentPaymentAddressId;
        this.currentPaymentAddressGooglePlaceId = currentPaymentAddressGooglePlaceId;
        this.currentPaymentAddressStreetNumber = currentPaymentAddressStreetNumber;
        this.currentPaymentAddressRoute = currentPaymentAddressRoute;
        this.currentPaymentAddressSecondaryAddress = currentPaymentAddressSecondaryAddress;
        this.currentPaymentAddressLocality = currentPaymentAddressLocality;
        this.currentPaymentAddressAdministrativeAreaLevel1 = currentPaymentAddressAdministrativeAreaLevel1;
        this.currentPaymentAddressAdministrativeAreaLevel2 = currentPaymentAddressAdministrativeAreaLevel2;
        this.currentPaymentAddressCountryLong = currentPaymentAddressCountryLong;
        this.currentPaymentAddressCountryShort = currentPaymentAddressCountryShort;
        this.currentPaymentAddressPostalCode = currentPaymentAddressPostalCode;
        this.currentPaymentAddressFormattedAddress = currentPaymentAddressFormattedAddress;
        this.currentPaymentAddressLatitude = currentPaymentAddressLatitude;
        this.currentPaymentAddressLongitude = currentPaymentAddressLongitude;
        this.created = created;
        this.updated = updated;
        this.deleted = deleted;
    }
}

class ShareableProfileResponse {

    constructor(
        memberId,
        displayName,
        email,
        phoneNumber,
        about
    ) {
        this.memberId = memberId;
        this.displayName = displayName;
        this.email = email;
        this.phoneNumber = phoneNumber;
        this.about = about;
    }
}

export default class ProfileAPIClient {
    constructor() {
        this.getProfileResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("PROFILE_COLLECTED", this.onProfileLoaded));
        this.submitProfileResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("NON_UNIQUE_DISPLAY_NAME", this.onNonUniqueDisplayName)
                    .set("NON_UNIQUE_EMAIL", this.onNonUniqueEmail)
                    .set("PROFILE_CREATED", this.onProfileCreated));
        this.getAdvertisementOwnerProfileResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("PROFILE_SHAREABLE_INFORMATION_COLLECTED", this.onAdvertisementOwnerShareableProfileLoaded)
                    .set("UNAUTHORIZED", this.onShareableProfileUnauthorized));

        this.getSpaceOwnerProfileResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("PROFILE_SHAREABLE_INFORMATION_COLLECTED", this.onSpaceOwnerShareableProfileLoaded)
                    .set("UNAUTHORIZED", this.onShareableProfileUnauthorized));
        this.editProfileResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("NON_UNIQUE_DISPLAY_NAME", this.onEditedProfileNonUniqueDisplayName)
                    .set("NON_UNIQUE_EMAIL", this.onEditedProfileNonUniqueEmail)
                    .set("PROFILE_UPDATED", this.onProfileEdited));
    }

    getProfile() {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/profile",
            {},
            {},
            this.getProfileResponseHandler);
    }

    getAdvertisementOwnerProfile(data) {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/profile/advertisement_owner/" + data.advertisementId,
            {},
            {},
            this.getAdvertisementOwnerProfileResponseHandler);
    }

    getSpaceOwnerProfile(data) {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/profile/space_owner/" + data.spaceId,
            {},
            {},
            this.getSpaceOwnerProfileResponseHandler);
    }

    submitProfile(profileData) {
        SecureRequestExecuter.executePOSTRequest(
            "/api/private/profile",
            {},
            {
                "display_name": profileData.displayName,
                "email": profileData.email,
                "phone_number": profileData.phoneNumber,
                "about": profileData.about,
                "payment_address_name": profileData.paymentAddressName,
                "google_place_id": profileData.currentPaymentAddressGooglePlaceId,
                "payment_address_secondary_address": profileData.currentPaymentAddressSecondaryAddress
            },
            {},
            this.submitProfileResponseHandler
        );
    }

    editProfile(profileData) {
        SecureRequestExecuter.executePUTRequest(
            "/api/private/profile",
            {},
            {
                "display_name": profileData.displayName,
                "email": profileData.email,
                "phone_number": profileData.phoneNumber,
                "about": profileData.about,
                "payment_address_name": profileData.paymentAddressName,
                "google_place_id": profileData.currentPaymentAddressGooglePlaceId,
                "payment_address_secondary_address": profileData.currentPaymentAddressSecondaryAddress
            },
            {},
            this.editProfileResponseHandler
        );
    }

    onNonUniqueDisplayName = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.PROFILE_CREATE_NON_UNIQUE_DISPLAY_NAME, {});
    };

    onNonUniqueEmail = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.PROFILE_CREATE_NON_UNIQUE_EMAIL, {});
    };

    onProfileCreated = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.PROFILE_CREATED, {});
    };

    onEditedProfileNonUniqueDisplayName = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.PROFILE_EDIT_NON_UNIQUE_DISPLAY_NAME, {});
    };

    onEditedProfileNonUniqueEmail = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.PROFILE_EDIT_NON_UNIQUE_EMAIL, {});
    };

    onProfileEdited = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.PROFILE_EDITED, {});
    };

    onProfileLoaded = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.PROFILE_LOADED,
            new ProfileResponse(
                responseBodyContents["profile_id"],
                responseBodyContents["member_id"],
                responseBodyContents["display_name"],
                responseBodyContents["email"],
                responseBodyContents["phone_number"],
                responseBodyContents["about"],
                responseBodyContents["payment_address_name"],
                responseBodyContents["current_payment_address_id"],
                responseBodyContents["current_payment_address_google_place_id"],
                responseBodyContents["current_payment_address_street_number"],
                responseBodyContents["current_payment_address_route"],
                responseBodyContents["current_payment_address_secondary_address"],
                responseBodyContents["current_payment_address_locality"],
                responseBodyContents["current_payment_address_administrative_area_level_1"],
                responseBodyContents["current_payment_address_administrative_area_level_2"],
                responseBodyContents["current_payment_address_country_long"],
                responseBodyContents["current_payment_address_country_short"],
                responseBodyContents["current_payment_address_postal_code"],
                responseBodyContents["current_payment_address_formatted_address"],
                responseBodyContents["current_payment_address_latitude"],
                responseBodyContents["current_payment_address_longitude"],
                responseBodyContents["created"],
                responseBodyContents["updated"],
                responseBodyContents["deleted"]));
    };

    onSpaceOwnerShareableProfileLoaded = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.SPACE_OWNER_SHAREABLE_PROFILE_LOADED,
            new ShareableProfileResponse(
                responseBodyContents["member_id"],
                responseBodyContents["display_name"],
                responseBodyContents["email"],
                responseBodyContents["phone_number"],
                responseBodyContents["about"]));
    };

    onAdvertisementOwnerShareableProfileLoaded = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.ADVERTISEMENT_OWNER_SHAREABLE_PROFILE_LOADED,
            new ShareableProfileResponse(
                responseBodyContents["member_id"],
                responseBodyContents["display_name"],
                responseBodyContents["email"],
                responseBodyContents["phone_number"],
                responseBodyContents["about"]));
    };


    onShareableProfileUnauthorized = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.SHAREABLE_PROFILE_UNAUTHORIZED,
            {});
    };

}